import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'business_head', label: 'Business Head' },
  { value: 'zonal_head', label: 'Zonal Head' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'assistant_manager', label: 'Assistant Manager' },
  { value: 'team_leader', label: 'Team Leader' },
  { value: 'sales_point_business', label: 'Sales Point (Business)' },
  { value: 'sales_point_individual', label: 'Sales Point (Individual)' },
];

export const ROLE_LABELS = Object.fromEntries(ROLES.map(r => [r.value, r.label]));
export const ROLE_OPTIONS = ROLES;

export const ROLE_HIERARCHY = {
  admin: 0, business_head: 1, zonal_head: 2, sales_manager: 3,
  assistant_manager: 4, team_leader: 5, sales_point_business: 6, sales_point_individual: 6,
};

export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [roleFeatures, setRoleFeatures] = useState([]);
  const [businessLines, setBusinessLines] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const identifyUser = useCallback(async (userData) => {
    try {
      const res = await axios.post(`${API}/auth/identify`, userData);
      setCurrentUser(res.data);
      return res.data;
    } catch (e) {
      console.error('Identity error:', e);
      return null;
    }
  }, []);

  const seedData = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/seed`);
      setSeeded(true);
      return res.data;
    } catch (e) {
      console.error('Seed error:', e);
      return null;
    }
  }, []);

  const fetchNotifications = useCallback(async (userId) => {
    try {
      const res = await axios.get(`${API}/notifications`, { params: { user_id: userId } });
      setNotifications(res.data);
    } catch (e) {
      console.error('Notifications error:', e);
    }
  }, []);

  const markNotifRead = useCallback(async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, null, { params: { user_id: currentUser?.id } });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.error(e);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.id) {
      fetchNotifications(currentUser.id);
    }
    if (currentUser?.role) {
      axios.get(`${API}/roles/${currentUser.role}/features`).then(res => {
        setRoleFeatures(res.data.features || []);
      }).catch(() => setRoleFeatures(['dashboard']));
    }
    if (currentUser) {
      axios.get(`${API}/business-lines`).then(res => {
        const lines = res.data || [];
        setBusinessLines(lines);
        const userBizIds = currentUser.assigned_businesses || [];
        const userLines = lines.filter(l => userBizIds.includes(l.id));
        if (userLines.length > 0 && !selectedBusiness) {
          setSelectedBusiness(userLines[0].id);
        } else if (lines.length > 0 && !selectedBusiness) {
          setSelectedBusiness(lines[0].id);
        }
      }).catch(() => setBusinessLines([]));
    }
  }, [currentUser, fetchNotifications]);

  const api = {
    login: (email) => axios.post(`${API}/auth/login`, { email }),
    validateSession: (token) => axios.get(`${API}/auth/validate-session`, { params: { token } }),
    getUsers: (params) => axios.get(`${API}/users`, { params }),
    getUser: (id) => axios.get(`${API}/users/${id}`),
    createUser: (data) => axios.post(`${API}/users`, data),
    updateUser: (id, data) => axios.put(`${API}/users/${id}`, data),
    deleteUser: (id) => axios.delete(`${API}/users/${id}`),

    getBusinessPlaces: (params) => axios.get(`${API}/business-places`, { params }),
    createBusinessPlace: (data) => axios.post(`${API}/business-places`, data),
    updateBusinessPlace: (id, data) => axios.put(`${API}/business-places/${id}`, data),
    deleteBusinessPlace: (id) => axios.delete(`${API}/business-places/${id}`),

    getSalesPoints: (params) => axios.get(`${API}/sales-points`, { params }),
    createSalesPoint: (data) => axios.post(`${API}/sales-points`, data),
    updateSalesPoint: (id, data) => axios.put(`${API}/sales-points/${id}`, data),
    deleteSalesPoint: (id) => axios.delete(`${API}/sales-points/${id}`),

    getVisits: (params) => axios.get(`${API}/visits`, { params }),
    createVisit: (data) => axios.post(`${API}/visits`, data),
    updateVisit: (id, data) => axios.put(`${API}/visits/${id}`, data),
    deleteVisit: (id) => axios.delete(`${API}/visits/${id}`),

    getDashboardStats: (params) => axios.get(`${API}/dashboard/stats`, { params }),
    getPerformanceAnalytics: (params) => axios.get(`${API}/performance/analytics`, { params }),

    getUserTree: () => axios.get(`${API}/users/tree`),

    requestBusinessPlace: (data) => axios.post(`${API}/business-places/request`, data),
    getApprovals: (params) => axios.get(`${API}/approvals`, { params }),
    approveRequest: (id, data) => axios.put(`${API}/approvals/${id}/approve`, data),
    rejectRequest: (id, data) => axios.put(`${API}/approvals/${id}/reject`, data),

    saveImportConfig: (data, role) => axios.post(`${API}/import/config?requester_role=${role}`, data),
    getImportConfigs: (role) => axios.get(`${API}/import/configs?requester_role=${role}`),
    fetchImportData: (data, role) => axios.post(`${API}/import/fetch?requester_role=${role}`, data),
    getImports: () => axios.get(`${API}/imports`),
    getImportDetail: (id) => axios.get(`${API}/imports/${id}`),

    getRoles: () => axios.get(`${API}/roles`),
    getRoleFeatures: (roleName) => axios.get(`${API}/roles/${roleName}/features`),
    createRole: (data) => axios.post(`${API}/roles`, data),
    updateRole: (id, data) => axios.put(`${API}/roles/${id}`, data),
    deleteRole: (id) => axios.delete(`${API}/roles/${id}`),
    getFeatures: () => axios.get(`${API}/features`),

    exportTeam: (zone) => `${API}/export/team${zone ? `?zone=${zone}` : ''}`,
    exportVisits: (zone, userId) => {
      const params = new URLSearchParams();
      if (zone) params.set('zone', zone);
      if (userId) params.set('user_id', userId);
      return `${API}/export/visits${params.toString() ? `?${params}` : ''}`;
    },
    exportSalesPoints: (zone, spType) => {
      const params = new URLSearchParams();
      if (zone) params.set('zone', zone);
      if (spType) params.set('sp_type', spType);
      return `${API}/export/sales-points${params.toString() ? `?${params}` : ''}`;
    },
    exportPerformance: (zone) => `${API}/export/performance${zone ? `?zone=${zone}` : ''}`,

    getBusinessLines: () => axios.get(`${API}/business-lines`),
    createBusinessLine: (data) => axios.post(`${API}/business-lines`, data),
    updateBusinessLine: (id, data) => axios.put(`${API}/business-lines/${id}`, data),
    deleteBusinessLine: (id) => axios.delete(`${API}/business-lines/${id}`),

    getTravelPlans: (params) => axios.get(`${API}/travel-plans`, { params }),
    getTravelPlan: (id) => axios.get(`${API}/travel-plans/${id}`),
    createTravelPlan: (data, userId) => axios.post(`${API}/travel-plans?user_id=${userId}`, data),
    updateTravelPlan: (id, data) => axios.put(`${API}/travel-plans/${id}`, data),
    approveTravelPlan: (id, action, remarks) => axios.put(`${API}/travel-plans/${id}/approve?action=${action}&remarks=${encodeURIComponent(remarks || '')}`),
    startTravelPlan: (id, selfie, lat, lng) => axios.put(`${API}/travel-plans/${id}/start?selfie=${encodeURIComponent(selfie || '')}&lat=${lat || 0}&lng=${lng || 0}`),
    checkinLocation: (id, idx, lat, lng) => axios.put(`${API}/travel-plans/${id}/checkin/${idx}?lat=${lat || 0}&lng=${lng || 0}`),
    checkoutLocation: (id, idx, lat, lng) => axios.put(`${API}/travel-plans/${id}/checkout/${idx}?lat=${lat || 0}&lng=${lng || 0}`),
    completeTravelPlan: (id) => axios.put(`${API}/travel-plans/${id}/complete`),
    uploadBills: (id, bills) => axios.put(`${API}/travel-plans/${id}/upload-bills`, bills),
    deleteTravelPlan: (id) => axios.delete(`${API}/travel-plans/${id}`),

    // Leads
    getLeads: (params) => axios.get(`${API}/leads`, { params }),
    getLead: (id) => axios.get(`${API}/leads/${id}`),
    createLead: (data, userId) => axios.post(`${API}/leads?user_id=${userId}`, data),
    updateLead: (id, data) => axios.put(`${API}/leads/${id}`, data),
    deleteLead: (id) => axios.delete(`${API}/leads/${id}`),

    // Potential Businesses
    getPotentialBusinesses: (params) => axios.get(`${API}/potential-businesses`, { params }),
    createPotentialBusiness: (data) => axios.post(`${API}/potential-businesses`, data),
    updatePotentialBusiness: (id, data) => axios.put(`${API}/potential-businesses/${id}`, data),
    deletePotentialBusiness: (id) => axios.delete(`${API}/potential-businesses/${id}`),

    // OTP
    sendOtp: (mobile) => axios.post(`${API}/otp/send?mobile=${encodeURIComponent(mobile)}`),
    verifyOtp: (mobile, code) => axios.post(`${API}/otp/verify?mobile=${encodeURIComponent(mobile)}&code=${code}`),

    // OCR
    ocrVisitingCard: (image) => axios.post(`${API}/ocr/visiting-card`, { image }),

    // Settings
    getSetting: (key) => axios.get(`${API}/settings/${key}`),
    updateSetting: (key, value) => axios.put(`${API}/settings/${key}`, { value }),

    // Expense Claims
    getExpenseClaims: (params) => axios.get(`${API}/expense-claims`, { params }),
    getExpenseClaim: (id) => axios.get(`${API}/expense-claims/${id}`),
    processExpenseClaim: (id, data) => axios.put(`${API}/expense-claims/${id}/action`, data),
    getExpenseClaimStats: (params) => axios.get(`${API}/expense-claims/stats/summary`, { params }),
    getExpenseAnalytics: (params) => axios.get(`${API}/expense-claims/analytics/trends`, { params }),

    // Expense Policies
    getExpensePolicies: (params) => axios.get(`${API}/expense-policies`, { params }),
    upsertExpensePolicy: (data) => axios.post(`${API}/expense-policies`, data),
    deleteExpensePolicy: (id) => axios.delete(`${API}/expense-policies/${id}`),
    getExpenseCycles: (params) => axios.get(`${API}/expense-cycles`, { params }),
    upsertExpenseCycle: (data) => axios.post(`${API}/expense-cycles`, data),

    // Travel Modes
    getTravelModes: (params) => axios.get(`${API}/travel-modes`, { params }),
    createTravelMode: (data) => axios.post(`${API}/travel-modes`, data),
    updateTravelMode: (id, data) => axios.put(`${API}/travel-modes/${id}`, data),
    deleteTravelMode: (id) => axios.delete(`${API}/travel-modes/${id}`),

    // Fuel Rates
    getFuelRates: (params) => axios.get(`${API}/fuel-rates`, { params }),
    upsertFuelRate: (data) => axios.post(`${API}/fuel-rates`, data),
    deleteFuelRate: (id) => axios.delete(`${API}/fuel-rates/${id}`),

    // Geolocation
    getGeoTolerances: (params) => axios.get(`${API}/geo-tolerance`, { params }),
    upsertGeoTolerance: (data) => axios.post(`${API}/geo-tolerance`, data),
    validateLocation: (data) => axios.post(`${API}/validate-location`, data),

    // Google Maps
    getGoogleMapsKey: () => axios.get(`${API}/google-maps-key`),
    getGoogleMapsKeyRaw: () => axios.get(`${API}/google-maps-key/raw`),
    updateGoogleMapsKey: (value) => axios.put(`${API}/google-maps-key`, { value }),

    // Fuel Claims
    getFuelSuggestion: (planId) => axios.get(`${API}/travel-plans/${planId}/fuel-suggestion`),
    submitFuelClaim: (planId, data) => axios.put(`${API}/travel-plans/${planId}/fuel-claim`, data),
  };

  const isAdmin = currentUser?.role === 'admin';
  const userZone = currentUser?.zone && currentUser?.zone !== 'All' ? currentUser?.zone : null;
  const roleLevel = ROLE_HIERARCHY[currentUser?.role] || 6;

  return (
    <AppContext.Provider value={{
      currentUser, setCurrentUser, identifyUser,
      notifications, fetchNotifications, markNotifRead, markAllRead,
      loading, setLoading, seeded, seedData, api,
      isAdmin, userZone, roleLevel, roleFeatures,
      businessLines, selectedBusiness, setSelectedBusiness,
    }}>
      {children}
    </AppContext.Provider>
  );
};
